import { z } from 'zod';

import { reportErrorToBugsnag } from '@air/utils-error';

import { describeZodIssues } from '~/components/v2/components/workspace/workspace-agent-panel/utils/describeZodIssues';

/**
 * Everything needed to attribute a result to the call it answers. Split from the outcome below so a
 * malformed `success` still leaves the result attributable, rather than detaching it from its tool
 * call and leaving that call rendering as in-flight forever.
 */
const ToolResultEnvelopeSchema = z.object({
  toolCallId: z.string().min(1),
  toolName: z.string().optional(),
});

export const ToolResultMetadataSchema = ToolResultEnvelopeSchema.extend({
  /** Outcome fields stay off the envelope so malformed values do not break tool attribution. */
  mutated: z.boolean().optional(),
  success: z.boolean().optional(),
});

export type ToolResultMetadata = z.infer<typeof ToolResultMetadataSchema>;

export type ParsedToolResult =
  | { outcome: 'readable'; metadata: ToolResultMetadata }
  /** Attributable, but the outcome field itself was unreadable, so the tool has to read as failed. */
  | { outcome: 'unreadable'; toolCallId: string };

/** Backend error string when a tool is interrupted by run cancellation. */
export const WORKSPACE_AGENT_TOOL_RESULT_TURN_CANCELLED_ERROR = 'Turn cancelled';

const ToolResultContentSchema = z.object({
  error: z.string().nullable().optional(),
});

export const isWorkspaceAgentCancelledToolResultContent = (content: string | null): boolean => {
  if (!content) {
    return false;
  }

  try {
    const parsed = JSON.parse(content);
    const result = ToolResultContentSchema.safeParse(parsed);

    if (!result.success) {
      return false;
    }

    const error = result.data.error;

    return (
      typeof error === 'string' &&
      error.trim().toLowerCase() === WORKSPACE_AGENT_TOOL_RESULT_TURN_CANCELLED_ERROR.toLowerCase()
    );
  } catch {
    return false;
  }
};

export const parseToolResultMetadata = (metadata: unknown): ParsedToolResult | null => {
  const parsed = ToolResultMetadataSchema.safeParse(metadata);

  if (parsed.success) {
    return { outcome: 'readable', metadata: parsed.data };
  }

  const envelope = ToolResultEnvelopeSchema.safeParse(metadata);

  // Neither report below carries the payload: it holds what the tool read or wrote, which is
  // workspace content. `toolName` and `toolCallId` name the call, and are what make one actionable.
  if (envelope.success) {
    reportErrorToBugsnag({
      error: new Error('Tool result outcome could not be parsed'),
      context: 'parseToolResultMetadata',
      metadata: { issues: describeZodIssues(parsed.error), toolName: envelope.data.toolName },
    });

    return { outcome: 'unreadable', toolCallId: envelope.data.toolCallId };
  }

  reportErrorToBugsnag({
    error: new Error('Tool result metadata could not be parsed'),
    context: 'parseToolResultMetadata',
    metadata: { issues: describeZodIssues(envelope.error) },
  });

  return null;
};
